import React from 'react';
import { Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';

// The demo countdown shown at the top of an individual student's screen.
//
// Presentational: `access` is fetched once by Layout via useIndividualAccess
// and passed in, so the strip and the expired screen share one request.
//
// Renders nothing at all for school students, staff and superadmins — the
// school flow is untouched by this feature.
export default function DemoAccessBanner({ access }) {
  if (!access?.is_individual) return null;

  const { mode, days_remaining: days, expires_on: expiresOn, course_name: courseName } = access;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null);

  // Expired states are handled by DemoExpiredScreen, which replaces the page
  // entirely — showing a strip as well would just repeat it.
  if (mode === 'demo_expired' || mode === 'expired') return null;

  if (mode === 'paid') {
    // Only worth a strip when the end is actually near; a student 200 days
    // into a year of access does not need a permanent reminder.
    if (days > 30) return null;
    return (
      <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-sm px-4 py-2 flex items-center gap-2 flex-wrap">
        <CheckCircle2 size={16} className="shrink-0 text-emerald-600" />
        <span>
          Your access to <strong>{courseName || 'your course'}</strong> renews in{' '}
          <strong>{days} {days === 1 ? 'day' : 'days'}</strong>
          {expiresOn ? ` — valid until ${fmt(expiresOn)}` : ''}.
        </span>
      </div>
    );
  }

  // Demo. Colour escalates as the window closes so the last few days actually
  // read as urgent instead of blending into the same neutral strip.
  const urgent = days <= 3;
  const warn = days <= 7;
  const tone = urgent
    ? 'bg-red-50 border-red-200 text-red-900'
    : warn
      ? 'bg-amber-50 border-amber-200 text-amber-900'
      : 'bg-indigo-50 border-indigo-200 text-indigo-900';
  const iconTone = urgent ? 'text-red-600' : warn ? 'text-amber-600' : 'text-indigo-600';

  return (
    <div className={`${tone} border-b text-sm px-4 py-2 flex items-center justify-between gap-3 flex-wrap`}>
      <span className="flex items-center gap-2">
        {urgent ? <AlertTriangle size={16} className={`shrink-0 ${iconTone}`} />
                : <Clock size={16} className={`shrink-0 ${iconTone}`} />}
        <span>
          Demo access
          {courseName ? <> to <strong>{courseName}</strong></> : null} —{' '}
          <strong>{days} {days === 1 ? 'day' : 'days'} left</strong>
          {expiresOn ? <span className="hidden sm:inline"> (ends {fmt(expiresOn)})</span> : null}
        </span>
      </span>
      <span className="text-xs opacity-80">
        Complete your payment to keep access after the demo ends.
      </span>
    </div>
  );
}
